import { FC } from "react";
import { ClipboardCheckIcon } from "lucide-react-native";

import { VnBox } from "@/components/VnBox";
import { VnCard } from "@/components/VnCard";
import { VnIcon } from "@/components/VnIcon";
import { VnText } from "@/components/VnText/VnText";
import { ApplicationCardStateType } from "@/domain/dashboardCards";

import {
  ContinueApplicationButton,
  NewQuickApplyButton,
  StartApplicationButton,
} from "./DashboardApplicationActionButtons";
import { DashboardCompactCard } from "./DashboardCompactCard";

type ApplicationNextStepCardPropsType = {
  state: ApplicationCardStateType;
  // When submitted applications exist, "New Quick Apply" lives in the
  // persistent Applications section header instead of this card.
  hasSubmittedApplications: boolean;
  isPrimary: boolean;
};

export const ApplicationNextStepCard: FC<ApplicationNextStepCardPropsType> = ({
  state,
  hasSubmittedApplications,
  isPrimary,
}) => {
  const isInProgress = state === "IN_PROGRESS";
  const newQuickApplyButton = !hasSubmittedApplications && (
    <NewQuickApplyButton />
  );

  // Secondary treatment demotes the main action to the `outline` variant
  // (border-sidebar) to match the prototype; primary keeps the prominent CTA.
  if (!isPrimary) {
    return (
      <DashboardCompactCard
        i18nKey={
          isInProgress
            ? "native:applicationSection.continueYourApplicationCompact"
            : "native:applicationSection.beginYourApplicationCompact"
        }
        actions={
          isInProgress ? (
            <>
              <ContinueApplicationButton
                variant="outline"
                i18nKey="common:continue"
              />
              {newQuickApplyButton}
            </>
          ) : (
            <StartApplicationButton variant="outline" />
          )
        }
      />
    );
  }

  return (
    <VnCard className="flex flex-col">
      <VnBox className="flex flex-row items-center gap-4">
        <VnBox className="rounded-md bg-black/5 p-6">
          <VnIcon as={ClipboardCheckIcon} size={42} strokeWidth={1} />
        </VnBox>

        <VnText
          variant="lg"
          i18nKey={
            isInProgress
              ? "native:applicationSection.continueYourApplicationProcess"
              : "native:applicationSection.beginYourApplicationProcess"
          }
        />
      </VnBox>

      <VnBox className="gap-3">
        {isInProgress ? (
          <>
            <ContinueApplicationButton />
            {newQuickApplyButton}
          </>
        ) : (
          <StartApplicationButton />
        )}
      </VnBox>
    </VnCard>
  );
};
